<script setup lang="ts">
import { computed, onBeforeMount, ref, toRaw } from 'vue';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { getTextVariant } from '@/utils/stringUtils';
import { useClientCopyComposable } from './useClientCopyComposable';
import { useModalComposable } from '@/views/composable';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import ClientCopyHeader from './ClientCopyHeader.vue';
import ClientCopyInfo from './ClientCopyInfo.vue';
import ClientCopyFlightDetail from './ClientCopyFlightDetail.vue';
import ClientCopyTourInfo from './ClientCopyTourInfo.vue';
import ClientCopyTourPricing from './ClientCopyTourPricing.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

interface Props {
  show: boolean;
  quote: object;
}
const props = defineProps<Props>();

const data = computed(() => props.quote);
const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(data.value.lang);
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { FlightTourImage } = useClientCopyComposable(data.value?.lang, data.value?.currency);
const { handleOpenErrorMessage } = useModalComposable();
const viewQuoteStore = useViewQuoteStore();
const myQuoteStore = useMyQuoteStore();
const logo = ref();
const totalRoom = ref(0);
const isEdit = ref(false);
const isEditPricing: any = ref([]);
const isLoading = ref(false);
const isPriceValid = ref(true);

const handleSubmitClientCopy = () => {
  if (isEdit.value) {
    toast.add({ severity: 'info', summary: '', detail: t('client_copy.save_price_before_submit'), life: 3000 });
    return;
  }
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isLoading.value = true;
  const quoteCopy: any = structuredClone(toRaw(props.quote));
  quoteCopy.urlLogo = logo.value || quoteCopy.urlLogo;
  viewQuoteStore
    .handleSubmitClientCopy(quoteCopy)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    })
    .finally(() => (isLoading.value = false));
};

onBeforeMount(() => {
  data.value?.multiDayTour?.forEach((multiDayTourItem: any, multiTourIndex: number) => {
    totalRoom.value += multiDayTourItem?.numOfRooms;
    isEditPricing.value[multiTourIndex] = [];
    multiDayTourItem?.tours.forEach((tour: any, tourIndex: number) => {
      isEditPricing.value[multiTourIndex][tourIndex] = [];
      tour.rooms?.forEach((room: any, indexRoom: number) => {
        isEditPricing.value[multiTourIndex][tourIndex][indexRoom] = false;
      });
    });
  });
});
</script>
<template>
  <Transition name="modal">
    <div v-if="show" class="client-copy">
      <div class="modal-mask">
        <LoadingSpinner v-if="isLoading" />
        <div class="relative modal-container client-copy-air" @click.stop>
          <div class="sticky top-0">
            <div class="absolute right-0 flex align-items-center justify-content-center w-3rem h-3rem border-round">
              <Button class="text-900" icon="pi pi-times" severity="success" text rounded @click="$emit('close')" />
            </div>
          </div>
          <div class="wrapper py-4 px-5">
            <div class="wrapper">
              <table class="w-full">
                <thead>
                  <tr>
                    <th class="hidden"></th>
                  </tr>
                </thead>
                <tbody>
                  <ClientCopyHeader v-model:logo="logo" :url="`${myQuoteStore?.getUrlUploadImg}?clientCopy=true`" :data="data" />
                  <ClientCopyInfo :data="data" :totalRoom="totalRoom" />
                </tbody>
              </table>
              <div v-for="(multiDayTourItem, indexMultiTour) in data?.multiDayTour" :key="indexMultiTour">
                <div v-for="(tour, indexTour) in multiDayTourItem.tours" :key="indexTour">
                  <table class="w-full">
                    <thead>
                      <tr>
                        <th class="hidden"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td class="py-3 flex">
                          <img alt="img" replace-type="media" :src="FlightTourImage" class="height-25px" />
                          <div class="text-4xl font-bold">
                            {{ $t('common.option') }} <span>{{ indexMultiTour + 1 }}</span>
                            <span>
                              ({{ $t('common.tour') }} <span>{{ indexTour + 1 }}</span
                              >) : {{ `${$t('client_copy.adults_count', multiDayTourItem.adtNo)}, ${$t('client_copy.children_count', multiDayTourItem.chdNo)} | ` }}
                              {{ `${$t('client_copy.rooms_count', multiDayTourItem.numOfRooms)}` }}
                            </span>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td class="text-2xl pt-1 pb-3">
                          <span>{{ getTextVariant(data?.lang, multiDayTourItem.gate, multiDayTourItem.gateFr) }}</span> {{ $t('common.to_normal') }}
                          <span>{{ getTextVariant(data?.lang, multiDayTourItem.dest, multiDayTourItem.destFr) }}</span>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <ClientCopyTourInfo :lang="data?.lang" :tours="tour" />
                        </td>
                      </tr>
                      <tr class="break-before">
                        <td colspan="3" class="pt-3 text-xl">{{ $t('common.flight_details') }}</td>
                      </tr>
                      <tr>
                        <td class="text-base">{{ `${$t('common.labels.departure_date')}: ${tour.flights[0].depDate}` }} | {{ `${$t('client_copy.return_date')}: ${tour.flights[tour.flights.length - 1].depDate}` }}</td>
                      </tr>
                      <tr>
                        <td colspan="3" class="pt-2">
                          <ClientCopyFlightDetail :flights="tour.flights" :lang="data?.lang" />
                        </td>
                      </tr>
                      <ClientCopyTourPricing
                        :tourInclusions="tour.tourInclusion"
                        :rooms="tour.rooms"
                        :data="data"
                        :optionIndex="indexMultiTour"
                        :tourIndex="indexTour"
                        v-model:isEdit="isEdit"
                        v-model:isPriceValid="isPriceValid"
                        v-model:isEditPricing="isEditPricing"
                      />
                    </tbody>
                  </table>
                </div>
                <div>
                  <HTMLEditor class="mb-3 w-full mt-1" v-model.trim="multiDayTourItem.comment" />
                </div>
              </div>
            </div>
            <div class="action-submit text-left">
              <Button v-if="authorizations?.my_quote?.client_copy_edit" :label="$t('common.buttons.submit')" class="mt-5" @click="handleSubmitClientCopy()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
